import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  QrCode,
  Plus,
  Key,
  Activity,
  Users,
  TrendingUp,
  MoreVertical,
  Copy,
  Ban,
  Trash2,
  CheckCircle,
  LogOut,
  RefreshCw,
  Package,
  CreditCard,
  DollarSign,
  Clock,
  XCircle,
  Eye,
  Settings,
  Mail,
  Building,
} from "lucide-react";
import Navbar from "@/components/modules/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Dialogs
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [showPaymentProof, setShowPaymentProof] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  
  // Package form
  const [packageForm, setPackageForm] = useState({
    name: "",
    description: "",
    price: "",
    price_idr: "",
    websites_count: "1",
    duration_months: "12",
    api_hits_limit: "0",
    features: "",
  });
  
  // Settings
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [smtpSettings, setSmtpSettings] = useState({
    host: "",
    port: "587",
    username: "",
    password: "",
    secure: false,
    require_tls: true,
    from_email: "",
    from_name: "QRForge",
  });
  const [bankSettings, setBankSettings] = useState({
    bank_name: "",
    account_number: "",
    account_holder: "",
    notes: "",
  });
  const [testEmail, setTestEmail] = useState("");

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const fetchData = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      setIsLoading(true);
      const [statsRes, tokensRes, usersRes, packagesRes, paymentsRes, chartRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/tokens`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/packages`, { headers }),
        axios.get(`${API}/admin/payments`, { headers }),
        axios.get(`${API}/admin/usage-chart`, { headers }),
      ]);

      setStats(statsRes.data);
      setTokens(tokensRes.data);
      setUsers(usersRes.data);
      setPackages(packagesRes.data);
      setPayments(paymentsRes.data);
      setChartData(chartRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  // Package functions
  const handleSavePackage = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    const payload = {
      name: packageForm.name,
      description: packageForm.description,
      price: parseFloat(packageForm.price),
      price_idr: packageForm.price_idr ? parseFloat(packageForm.price_idr) : null,
      websites_count: parseInt(packageForm.websites_count),
      duration_months: parseInt(packageForm.duration_months),
      api_hits_limit: parseInt(packageForm.api_hits_limit) || 0,
      features: packageForm.features.split("\n").filter((f) => f.trim()),
    };

    try {
      if (editingPackage) {
        await axios.patch(`${API}/admin/packages/${editingPackage.id}`, payload, { headers });
        toast.success("Package updated");
      } else {
        await axios.post(`${API}/admin/packages`, payload, { headers });
        toast.success("Package created");
      }
      setShowPackageDialog(false);
      setEditingPackage(null);
      setPackageForm({ name: "", description: "", price: "", price_idr: "", websites_count: "1", duration_months: "12", api_hits_limit: "0", features: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to save package");
    }
  };

  const handleDeletePackage = async (id) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.delete(`${API}/admin/packages/${id}`, { headers });
      toast.success("Package deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete package");
    }
  };
  
  // Settings functions
  const fetchSettings = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    
    try {
      const [smtpRes, bankRes] = await Promise.all([
        axios.get(`${API}/admin/settings/smtp`, { headers }),
        axios.get(`${API}/admin/settings/bank-account`, { headers }),
      ]);
      setSmtpSettings(smtpRes.data);
      setBankSettings(bankRes.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };
  
  const handleSaveSmtpSettings = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    
    const formData = new FormData();
    formData.append("host", smtpSettings.host);
    formData.append("port", smtpSettings.port);
    formData.append("username", smtpSettings.username);
    formData.append("password", smtpSettings.password);
    formData.append("secure", smtpSettings.secure);
    formData.append("require_tls", smtpSettings.require_tls);
    formData.append("from_email", smtpSettings.from_email);
    formData.append("from_name", smtpSettings.from_name);
    
    try {
      await axios.post(`${API}/admin/settings/smtp`, formData, { headers });
      toast.success("SMTP settings saved");
    } catch (error) {
      toast.error("Failed to save SMTP settings");
    }
  };
  
  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error("Please enter a test email address");
      return;
    }
    
    const headers = getAuthHeaders();
    if (!headers) return;
    
    const formData = new FormData();
    formData.append("test_email", testEmail);
    
    try {
      const res = await axios.post(`${API}/admin/settings/smtp/test`, formData, { headers });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    }
  };
  
  const handleSaveBankSettings = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    
    const formData = new FormData();
    formData.append("bank_name", bankSettings.bank_name);
    formData.append("account_number", bankSettings.account_number);
    formData.append("account_holder", bankSettings.account_holder);
    formData.append("notes", bankSettings.notes);
    
    try {
      await axios.post(`${API}/admin/settings/bank-account`, formData, { headers });
      toast.success("Bank account settings saved");
    } catch (error) {
      toast.error("Failed to save bank account settings");
    }
  };

  // Payment functions
  const handleApprovePayment = async (id) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.patch(`${API}/admin/payments/${id}/approve`, {}, { headers });
      toast.success("Payment approved, tokens created");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve payment");
    }
  };

  const handleRejectPayment = async (id) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    const notes = prompt("Rejection reason (optional):");

    try {
      await axios.patch(`${API}/admin/payments/${id}/reject?notes=${encodeURIComponent(notes || "")}`, {}, { headers });
      toast.success("Payment rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject payment");
    }
  };

  // User functions
  const handleToggleUserStatus = async (userId, currentStatus) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    const newStatus = currentStatus === "active" ? "suspended" : "active";

    try {
      await axios.patch(`${API}/admin/users/${userId}/status?status=${newStatus}`, {}, { headers });
      toast.success(`User ${newStatus === "active" ? "activated" : "suspended"}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  // Token functions
  const handleToggleTokenStatus = async (tokenId, currentStatus) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    const newStatus = currentStatus === "active" ? "disabled" : "active";

    try {
      await axios.patch(`${API}/admin/tokens/${tokenId}`, { status: newStatus }, { headers });
      toast.success(`Token ${newStatus === "active" ? "enabled" : "disabled"}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update token");
    }
  };

  const handleDeleteToken = async (tokenId) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.delete(`${API}/admin/tokens/${tokenId}`, { headers });
      toast.success("Token deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete token");
    }
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    toast.success("Token copied");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]">
     
     <Navbar />

      <main className="container mx-auto px-4 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="stats-card tech-border">
            <Users className="w-5 h-5 text-blue-500 mb-2" />
            <h3>Total Users</h3>
            <p>{stats?.total_users || 0}</p>
          </div>
          <div className="stats-card tech-border">
            <Key className="w-5 h-5 text-primary-500 mb-2" />
            <h3>Active Tokens</h3>
            <p>{stats?.active_tokens || 0}</p>
          </div>
          <div className="stats-card tech-border">
            <Activity className="w-5 h-5 text-green-500 mb-2" />
            <h3>Today's Requests</h3>
            <p>{stats?.requests_today || 0}</p>
          </div>
          <div className="stats-card tech-border">
            <DollarSign className="w-5 h-5 text-emerald-500 mb-2" />
            <h3>Total Revenue</h3>
            <p>${stats?.total_revenue?.toFixed(2) || "0.00"}</p>
          </div>
          <div className="stats-card tech-border">
            <Clock className="w-5 h-5 text-yellow-500 mb-2" />
            <h3>Pending Payments</h3>
            <p>{stats?.pending_payments || 0}</p>
          </div>
        </div>

        {/* Chart */}
        <div className="tech-border rounded-lg p-6 bg-dark-foreground mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary-500" />
            API Usage (Last 7 Days)
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="date" stroke="#71717A" fontSize={12} />
                <YAxis stroke="#71717A" fontSize={12} />
                <Tooltip contentStyle={{ backgroundColor: "#18181B", border: "1px solid #27272A", borderRadius: "8px" }} />
                <Line type="monotone" dataKey="requests" stroke="#F97316" strokeWidth={2} dot={{ fill: "#F97316" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="payments" className="w-full">
          <TabsList className="bg-dark-foreground border border-zinc-800 mb-6">
            <TabsTrigger value="payments" className="data-[state=active]:bg-primary-500/20">
              <CreditCard className="w-4 h-4 mr-2" />
              Payments
              {stats?.pending_payments > 0 && (
                <Badge className="ml-2 bg-yellow-500/20 text-yellow-400">{stats.pending_payments}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="packages" className="data-[state=active]:bg-primary-500/20">
              <Package className="w-4 h-4 mr-2" />
              Packages
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-primary-500/20">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="tokens" className="data-[state=active]:bg-primary-500/20">
              <Key className="w-4 h-4 mr-2" />
              Tokens
            </TabsTrigger>
          </TabsList>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <div className="space-y-4">
              {payments.length === 0 ? (
                <div className="tech-border rounded-lg p-12 bg-dark-foreground text-center">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                  <p className="text-zinc-400">No payments yet</p>
                </div>
              ) : (
                payments.map((payment) => (
                  <div key={payment.id} className="tech-border rounded-lg p-4 bg-dark-foreground">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-white">{payment.user?.name || "Unknown User"}</h3>
                          <Badge
                            className={
                              payment.status === "approved" ? "status-active" :
                              payment.status === "rejected" ? "status-disabled" :
                              "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                            }
                          >
                            {payment.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-zinc-400">
                          {payment.package?.name} - ${payment.amount} via {payment.payment_method}
                        </p>
                        {payment.created_at !== undefined && (
                          <p className="text-xs text-zinc-500 mt-1">
                            {payment.user?.email} • {new Date(payment.created_at).toLocaleString()}
                          </p>
                        )}
                      </div>

                      {payment.status === "pending" && (
                        <div className="flex items-center gap-2">
                          {payment.proof_image_base64 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowPaymentProof(payment.proof_image_base64)}
                              className="border-zinc-700"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View Proof
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => handleApprovePayment(payment.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRejectPayment(payment.id)}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Packages Tab */}
          <TabsContent value="packages">
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => {
                  setEditingPackage(null);
                  setPackageForm({ name: "", description: "", price: "", price_idr: "", websites_count: "1", duration_months: "12", api_hits_limit: "0", features: "" });
                  setShowPackageDialog(true);
                }}
                className="bg-primary-500 hover:bg-primary-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Package
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages.map((pkg) => (
                <div key={pkg.id} className="tech-border rounded-lg p-6 bg-dark-foreground">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-white">{pkg.name}</h3>
                      <Badge className={pkg.is_active ? "status-active" : "status-disabled"}>
                        {pkg.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-zinc-400">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-dark-foreground border-zinc-800">
                        <DropdownMenuItem
                          onClick={() => {
                            setEditingPackage(pkg);
                            setPackageForm({
                              name: pkg.name,
                              description: pkg.description,
                              price: pkg.price.toString(),
                              price_idr: (pkg.price_idr || "").toString(),
                              websites_count: pkg.websites_count.toString(),
                              duration_months: pkg.duration_months.toString(),
                              api_hits_limit: (pkg.api_hits_limit || 0).toString(),
                              features: pkg.features.join("\n"),
                            });
                            setShowPackageDialog(true);
                          }}
                          className="text-zinc-300"
                        >
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeletePackage(pkg.id)}
                          className="text-red-400"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <p className="text-sm text-zinc-400 mb-4">{pkg.description}</p>

                  <div className="mb-2">
                    <span className="text-2xl font-bold text-primary-500">${pkg.price}</span>
                    <span className="text-zinc-500 text-sm">/mo</span>
                    <span className="text-zinc-400 text-sm ml-2">
                      (${pkg.price * 12}/year)
                    </span>
                  </div>
                  
                  {pkg.price_idr && (
                    <div className="mb-2 text-sm">
                      <span className="text-green-400">IDR {pkg.price_idr.toLocaleString()}</span>
                      <span className="text-zinc-500">/mo</span>
                      <span className="text-zinc-400 ml-2">
                        (IDR {(pkg.price_idr * 12).toLocaleString()}/year)
                      </span>
                    </div>
                  )}
                  
                  <div className="text-xs text-zinc-500 mb-2">
                    {pkg.websites_count} API Token(s) • {pkg.duration_months} months
                  </div>
                  
                  <div className="text-xs text-zinc-500 mb-2">
                    API Hits: {(pkg.api_hits_limit === 0 || !pkg.api_hits_limit) ? "Unlimited" : `${pkg.api_hits_limit.toLocaleString()}/month`}
                  </div>

                  <div className="text-xs text-zinc-500">
                    Features: {pkg.features.length}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-4">
              {users.length === 0 ? (
                <div className="tech-border rounded-lg p-12 bg-dark-foreground text-center">
                  <Users className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                  <p className="text-zinc-400">No users yet</p>
                </div>
              ) : (
                users.map((user) => (
                  <div key={user.id} className="tech-border rounded-lg p-4 bg-dark-foreground">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold text-white">{user.name}</h3>
                          <Badge className={user.status === "active" ? "status-active" : "status-disabled"}>
                            {user.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-zinc-400">{user.email}</p>
                        {user.company && <p className="text-xs text-zinc-500">{user.company}</p>}
                        <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                          <span>Tokens: {user.tokens_count}</span>
                          <span>Total Spent: ${user.total_spent?.toFixed(2) || "0.00"}</span>
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleUserStatus(user.id, user.status)}
                        className={user.status === "active" ? "border-red-500/50 text-red-400" : "border-green-500/50 text-green-400"}
                      >
                        {user.status === "active" ? (
                          <>
                            <Ban className="w-4 h-4 mr-1" />
                            Suspend
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Activate
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Tokens Tab */}
          <TabsContent value="tokens">
            <div className="space-y-4">
              {tokens.length === 0 ? (
                <div className="tech-border rounded-lg p-12 bg-dark-foreground text-center">
                  <Key className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                  <p className="text-zinc-400">No tokens yet</p>
                </div>
              ) : (
                tokens.map((token) => (
                  <div key={token.id} className="tech-border rounded-lg p-4 bg-dark-foreground">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold text-white">{token.name}</h3>
                          <Badge className={token.status === "active" ? "status-active" : "status-disabled"}>
                            {token.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-zinc-400">{token.user?.name || "Unknown"} ({token.user?.email})</p>
                        <code className="text-xs text-primary-400 font-mono">{token.token.substring(0, 30)}...</code>
                        <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                          <span>Used: {token.usage_count}x</span>
                          <span>Domains: {token.whitelisted_domains?.length || 0}</span>
                          {token.expires_at && <span>Expires: {new Date(token.expires_at).toLocaleDateString()}</span>}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => copyToken(token.token)} className="text-zinc-400">
                          <Copy className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-zinc-400">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-dark-foreground border-zinc-800">
                            <DropdownMenuItem
                              onClick={() => handleToggleTokenStatus(token.id, token.status)}
                              className="text-zinc-300"
                            >
                              {token.status === "active" ? "Disable" : "Enable"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteToken(token.id)}
                              className="text-red-400"
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Package Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="bg-dark-foreground border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingPackage ? "Edit Package" : "Create Package"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300">Package Name</Label>
              <Input
                value={packageForm.name}
                onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                placeholder="Pro Plan"
                className="mt-1 bg-zinc-950 border-zinc-800"
              />
            </div>

            <div>
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                value={packageForm.description}
                onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                placeholder="Best for growing businesses"
                className="mt-1 bg-zinc-950 border-zinc-800"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300">Price USD (per month)</Label>
                <Input
                  type="number"
                  value={packageForm.price}
                  onChange={(e) => setPackageForm({ ...packageForm, price: e.target.value })}
                  placeholder="10"
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
                <p className="text-xs text-zinc-500 mt-1">Required. Annual: ${packageForm.price ? (parseFloat(packageForm.price) * 12).toFixed(0) : 0}</p>
              </div>
              <div>
                <Label className="text-zinc-300">Price IDR (per month)</Label>
                <Input
                  type="number"
                  value={packageForm.price_idr}
                  onChange={(e) => setPackageForm({ ...packageForm, price_idr: e.target.value })}
                  placeholder="150000"
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
                <p className="text-xs text-zinc-500 mt-1">Optional. If empty, auto-convert from USD</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-zinc-300">API Tokens</Label>
                <Input
                  type="number"
                  value={packageForm.websites_count}
                  onChange={(e) => setPackageForm({ ...packageForm, websites_count: e.target.value })}
                  placeholder="1"
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-300">API Hits Limit (per month)</Label>
              <Input
                type="number"
                value={packageForm.api_hits_limit}
                onChange={(e) => setPackageForm({ ...packageForm, api_hits_limit: e.target.value })}
                placeholder="0 = Unlimited"
                className="mt-1 bg-zinc-950 border-zinc-800"
              />
              <p className="text-xs text-zinc-500 mt-1">Enter 0 for unlimited API hits</p>
            </div>

            <div>
              <Label className="text-zinc-300">Features (one per line)</Label>
              <Textarea
                value={packageForm.features}
                onChange={(e) => setPackageForm({ ...packageForm, features: e.target.value })}
                placeholder="Unlimited QR codes&#10;Priority support&#10;Custom branding"
                rows={4}
                className="mt-1 bg-zinc-950 border-zinc-800"
              />
            </div>

            <Button onClick={handleSavePackage} className="w-full bg-primary-500 hover:bg-primary-600">
              {editingPackage ? "Update Package" : "Create Package"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Proof Dialog */}
      <Dialog open={!!showPaymentProof} onOpenChange={() => setShowPaymentProof(null)}>
        <DialogContent className="bg-dark-foreground border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Payment Proof</DialogTitle>
          </DialogHeader>
          {showPaymentProof && (
            <img
              src={`data:image/png;base64,${showPaymentProof}`}
              alt="Payment Proof"
              className="w-full rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="bg-dark-foreground border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Admin Settings
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="smtp" className="mt-4">
            <TabsList className="bg-zinc-950 mb-4">
              <TabsTrigger value="smtp" className="data-[state=active]:bg-primary-500">
                <Mail className="w-4 h-4 mr-2" />
                SMTP
              </TabsTrigger>
              <TabsTrigger value="bank" className="data-[state=active]:bg-primary-500">
                <Building className="w-4 h-4 mr-2" />
                Bank Account
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="smtp" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">SMTP Host</Label>
                  <Input
                    value={smtpSettings.host}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
                    placeholder="smtp.example.com"
                    className="mt-1 bg-zinc-950 border-zinc-800"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Port</Label>
                  <Input
                    value={smtpSettings.port}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, port: e.target.value })}
                    placeholder="587"
                    className="mt-1 bg-zinc-950 border-zinc-800"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Username</Label>
                  <Input
                    value={smtpSettings.username}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, username: e.target.value })}
                    placeholder="user@example.com"
                    className="mt-1 bg-zinc-950 border-zinc-800"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Password</Label>
                  <Input
                    type="password"
                    value={smtpSettings.password}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, password: e.target.value })}
                    placeholder="••••••••"
                    className="mt-1 bg-zinc-950 border-zinc-800"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">From Email</Label>
                  <Input
                    value={smtpSettings.from_email}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, from_email: e.target.value })}
                    placeholder="noreply@example.com"
                    className="mt-1 bg-zinc-950 border-zinc-800"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">From Name</Label>
                  <Input
                    value={smtpSettings.from_name}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, from_name: e.target.value })}
                    placeholder="QRForge"
                    className="mt-1 bg-zinc-950 border-zinc-800"
                  />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-zinc-300">
                  <input
                    type="checkbox"
                    checked={smtpSettings.require_tls}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, require_tls: e.target.checked })}
                    className="rounded border-zinc-700"
                  />
                  Require TLS
                </label>
                <label className="flex items-center gap-2 text-zinc-300">
                  <input
                    type="checkbox"
                    checked={smtpSettings.secure}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, secure: e.target.checked })}
                    className="rounded border-zinc-700"
                  />
                  SSL/Secure
                </label>
              </div>
              <Button onClick={handleSaveSmtpSettings} className="w-full bg-primary-500 hover:bg-primary-600">
                Save SMTP Settings
              </Button>
              
              <div className="border-t border-zinc-800 pt-4 mt-4">
                <Label className="text-zinc-300">Test Email</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    className="bg-zinc-950 border-zinc-800"
                  />
                  <Button onClick={handleTestEmail} variant="outline" className="border-zinc-700">
                    Send Test
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="bank" className="space-y-4">
              <div>
                <Label className="text-zinc-300">Bank Name</Label>
                <Input
                  value={bankSettings.bank_name}
                  onChange={(e) => setBankSettings({ ...bankSettings, bank_name: e.target.value })}
                  placeholder="Bank Central Asia (BCA)"
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Account Number</Label>
                <Input
                  value={bankSettings.account_number}
                  onChange={(e) => setBankSettings({ ...bankSettings, account_number: e.target.value })}
                  placeholder="1234567890"
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Account Holder Name</Label>
                <Input
                  value={bankSettings.account_holder}
                  onChange={(e) => setBankSettings({ ...bankSettings, account_holder: e.target.value })}
                  placeholder="PT QRForge Indonesia"
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Additional Notes</Label>
                <Textarea
                  value={bankSettings.notes}
                  onChange={(e) => setBankSettings({ ...bankSettings, notes: e.target.value })}
                  placeholder="Include your Order ID in the transfer notes"
                  rows={3}
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
              </div>
              <Button onClick={handleSaveBankSettings} className="w-full bg-primary-500 hover:bg-primary-600">
                Save Bank Account Settings
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
